import React, { useContext, useState } from 'react'
import Grid from '@material-ui/core/Grid'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'
import DateFnsAdapter from '@date-io/date-fns'

import { KeyboardTimePicker, KeyboardDatePicker } from '@material-ui/pickers'

import {
  DynamicCheckboxOption,
  DynamicDateField,
  FormSection,
  DynamicRadioGroup,
  DynamicTextField,
  FieldTypes,
  DynamicFormGroup,
  DynamicRadioOption,
  FormCondition,
  InputTypes,
  DynamicSelectOption,
  DateFormats
} from './interfaces/Form'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import { FormGroup, Theme } from '@material-ui/core'
import FormControl from '@material-ui/core/FormControl'
import FormLabel from '@material-ui/core/FormLabel'
import FormHelperText from '@material-ui/core/FormHelperText'
import { useEffect } from 'react'
import DynamicFields from './DynamicFields'
import TextField from '@material-ui/core/TextField'
import MenuItem from '@material-ui/core/MenuItem'
import DynamicFormContext from './provider/DynamicFormContext'
import InputAdornment from '@material-ui/core/InputAdornment'
import Autocomplete from '@material-ui/lab/Autocomplete'
import SuggestionsMenu from '../SuggestionsMenu'
import { UneeqContext } from 'uneeq-react-core'

interface DynamicFieldProps {
  field:
    | DynamicCheckboxOption
    | DynamicRadioGroup
    | DynamicFormGroup
    | DynamicTextField
    | DynamicDateField
  index: number
  fullWidth?: boolean
  useStyles: any
  keyName: string
  allSuggestedResponses: any
  theme: Theme
}

/**
 * Check list of conditions are satisfied against the form data state
 *
 * @param data The form data state from the JSON input
 * @param conditions Object of conditions where each property matches a property in the Data param. Value is compared for equality.
 * @param index The current entity index for data inside MultiCard form section types. Values greater than -1 match the index of each entity in a card list.
 * @returns
 */
const hasConditionsMet = (
  data: any,
  conditions: FormCondition,
  index: number = -1
) => {
  let conditionSuccessCount = 0
  const conditionKeys = Object.keys(conditions)

  conditionKeys.forEach((conditionKey: string) => {
    const key = index > -1 ? `${conditionKey}-${index}` : conditionKey

    if (data[key] === conditions[conditionKey]) {
      conditionSuccessCount++
    }
  })

  return conditionSuccessCount === conditionKeys.length
}

const DynamicField: React.FC<DynamicFieldProps> = ({
  field,
  index = -1,
  fullWidth = true,
  useStyles,
  keyName,
  allSuggestedResponses,
  theme
}) => {
  const [show, setShow] = useState(false)
  const { formData, setFormData } = useContext(DynamicFormContext)
  const classes = useStyles()
  const dateFns = new DateFnsAdapter()
  const dateFormat = field.format ? field.format : DateFormats.Date
  const timeFormat = field.format ? field.format : DateFormats.Time
  const { sendText, dispatch } = useContext(UneeqContext)

  const [formDataDate, setFormDataDate] = React.useState<Date>(
    formData[field.key]
      ? dateFns.parse(formData[field.key], dateFormat)
      : field.value
      ? dateFns.parse(field.value, dateFormat)
      : new Date()
  )

  const [formDataTime, setFormDataTime] = React.useState<Date>(
    formData[field.key]
      ? dateFns.parse(formData[field.key], timeFormat)
      : field.value
      ? dateFns.parse(field.value, timeFormat)
      : new Date()
  )

  const handleDateChange = (date: Date | null) => {
    if (date === null) {
      return
    }

    const dateString = dateFns.format(date, dateFormat)
    setFormDataDate(date)
    setFormData(dateString)
  }

  const handleTimeChange = (date: Date | null) => {
    if (date === null) {
      return
    }

    const timeString = dateFns.format(date, timeFormat)
    setFormDataTime(date)
    setFormData(timeString)
  }

  const suggestedResponses = allSuggestedResponses
    ? allSuggestedResponses[field.key]
    : undefined

  // TODO: Fix issue where show state of field doesn't update correctly when conditionsMet changes
  useEffect(() => {
    if (field.showWhenMet && Object.keys(field.showWhenMet).length > 0) {
      const conditionsMet = hasConditionsMet(
        formData,
        field?.showWhenMet,
        index
      )

      setShow(conditionsMet)
    } else {
      setShow(true)
    }
  }, [formData, field, field?.showWhenMet, index])

  const getTextfield = function(
    field: DynamicTextField,
    data: any,
    setData: (data: any) => void,
    index: number = -1
  ) {
    const isSelect = field.inputType === InputTypes.Select
    return isSelect ? (
      <Grid key={field.key} container className={classes.formControl}>
        <Autocomplete
          id={keyName}
          options={field.selectOptions as DynamicSelectOption[]}
          getOptionLabel={(option: DynamicSelectOption) => option.label}
          autoHighlight
          className={classes.autocomplete}
          autoComplete={true}
          size="medium"
          fullWidth={fullWidth}
          onChange={(event: Object, newValue) => {
            const dataKey = isSelect ? keyName : field.key
            setData({ ...data, [dataKey]: newValue?.value })
          }}
          renderInput={params => (
            <TextField
              {...params}
              required={field.required}
              variant="standard"
              label={field.label}
              defaultValue={data[keyName]}
              autoComplete={
                field.autoComplete === 'off' ? 'new-password' : 'true'
              }
              helperText={suggestedResponses ? '' : field.helperText}
            ></TextField>
          )}
        />
        {suggestedResponses ? (
          <SuggestionsMenu
            helperText={field.helperText}
            items={suggestedResponses}
            inner={false}
            askHandler={sendText}
            theme={theme}
          />
        ) : (
          ''
        )}
      </Grid>
    ) : (
      <TextField
        key={field.key}
        required={field.required}
        variant="standard"
        id={keyName}
        name={keyName}
        defaultValue={data[keyName]}
        label={field.label}
        InputProps={{
          endAdornment: suggestedResponses ? (
            <InputAdornment position="end">
              <SuggestionsMenu
                helperText={field.helperText}
                items={suggestedResponses}
                askHandler={sendText}
                theme={theme}
              />
            </InputAdornment>
          ) : (
            ''
          )
        }}
        size="medium"
        fullWidth={fullWidth}
        autoComplete={field.autoComplete}
        inputProps={field.inputProps}
        helperText={suggestedResponses ? '' : field.helperText}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          const dataKey = isSelect ? keyName : field.key
          setData({ ...data, [dataKey]: event.target.value })
        }}
      >
        {field.selectOptions &&
          field.selectOptions.map((option: DynamicSelectOption) => (
            <MenuItem key={option.key} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
      </TextField>
    )
  }

  const getRadio = function(
    field: DynamicRadioOption,
    suggestedResponses?: string[]
  ) {
    return (
      <Grid key={field.key} container alignItems="flex-end">
        <FormControlLabel
          control={<Radio />}
          value={field.value}
          label={field.label}
        />
        {suggestedResponses ? (
          <SuggestionsMenu
            items={suggestedResponses}
            inner={true}
            askHandler={sendText}
            theme={theme}
          />
        ) : (
          ''
        )}
      </Grid>
    )
  }

  const getCheckbox = function(
    field: DynamicCheckboxOption,
    keyName: string,
    data: any,
    setData: (data: any) => void,
    index: number = -1,
    checkboxSuggestedResponses?: string[]
  ) {
    const checked = data[keyName] ? true : false
    return (
      <Grid
        key={field.key}
        container
        alignItems="flex-end"
        justifyContent="space-between"
      >
        <FormControlLabel
          className={classes.formControlLabel}
          control={
            <Checkbox
              key={field.key}
              checked={checked}
              value={field.value}
              name={keyName}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setData({ ...data, [keyName]: event.target.checked })
              }}
            />
          }
          label={field.label}
        />
        {checkboxSuggestedResponses ? (
          <SuggestionsMenu
            items={checkboxSuggestedResponses}
            inner={true}
            askHandler={sendText}
            theme={theme}
          />
        ) : (
          ''
        )}
      </Grid>
    )
  }

  const getRadioGroup = function(
    group: DynamicRadioGroup,
    data: any,
    setData: (data: any) => void,
    index: number = -1,
    allSuggestedResponses?: any
  ) {
    return (
      <Grid
        key={field.key}
        item
        xs={12}
        sm={fullWidth ? 12 : 6}
        style={{ paddingRight: 20 }}
      >
        <RadioGroup
          row={group.horizontal}
          aria-label={group.label}
          name={keyName}
          className={classes.radioGroup}
          defaultValue={data[keyName]}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setData({ ...data, [keyName]: event.target.value })
          }}
        >
          {group.fields.map((field: DynamicRadioOption) => {
            return getRadio(
              field,
              allSuggestedResponses
                ? allSuggestedResponses[field.key]
                : undefined
            )
          })}
        </RadioGroup>
      </Grid>
    )
  }

  const getFormGroup = function(
    group: DynamicFormGroup,
    data: any,
    setData: (data: any) => void,
    index: number = -1,
    allSuggestedResponses?: any
  ) {
    return (
      <Grid
        key={field.key}
        item
        xs={12}
        sm={fullWidth ? 12 : 6}
        style={{ paddingRight: 20 }}
      >
        <FormGroup row={group.horizontal}>
          {group.fields.map((field: DynamicCheckboxOption) => {
            return getCheckbox(
              field,
              field.key,
              data,
              setData,
              index,
              allSuggestedResponses
                ? allSuggestedResponses[field.key]
                : undefined
            )
          })}
        </FormGroup>
      </Grid>
    )
  }

  const getDateField = function(
    field: DynamicDateField,
    data: Date | null,
    setData: (data: Date | null) => void,
    index: number = -1
  ) {
    return (
      <Grid container alignItems="flex-end" className={classes.formControl}>
        <KeyboardDatePicker
          margin="normal"
          id={keyName}
          label={field.label}
          format={dateFormat}
          value={data}
          onChange={handleDateChange}
          KeyboardButtonProps={{
            'aria-label': 'change date'
          }}
        />
        {suggestedResponses ? (
          <SuggestionsMenu
            items={suggestedResponses}
            inner={false}
            askHandler={sendText}
            theme={theme}
          />
        ) : (
          ''
        )}
      </Grid>
    )
  }

  const getTimeField = function(
    field: DynamicDateField,
    data: any,
    setData: (data: Date | null) => void,
    index: number = -1
  ) {
    return (
      <Grid container alignItems="flex-end" className={classes.formControl}>
        <KeyboardTimePicker
          margin="normal"
          id={keyName}
          label={field.label}
          format={timeFormat}
          value={data}
          onChange={handleTimeChange}
          KeyboardButtonProps={{
            'aria-label': 'change time'
          }}
        />
        {suggestedResponses ? (
          <SuggestionsMenu
            items={suggestedResponses}
            inner={false}
            askHandler={sendText}
            theme={theme}
          />
        ) : (
          ''
        )}
      </Grid>
    )
  }

  if (!show) {
    return <React.Fragment />
  }

  switch (field.type) {
    case FieldTypes.Textfield:
      return (
        <Grid key={field.key} item xs={12} sm={fullWidth ? 12 : 6}>
          {getTextfield(
            field as DynamicCheckboxOption,
            formData,
            setFormData,
            index
          )}
        </Grid>
      )

    case FieldTypes.Checkbox:
      return (
        <Grid key={field.key} item xs={12} sm={fullWidth ? 12 : 6}>
          <FormGroup row={true}>
            {getCheckbox(
              field as DynamicCheckboxOption,
              keyName,
              formData,
              setFormData,
              index,
              allSuggestedResponses
                ? allSuggestedResponses[field.key]
                : undefined
            )}
          </FormGroup>
        </Grid>
      )

    case FieldTypes.RadioGroup:
      return (
        <Grid key={field.key} item xs={12} sm={fullWidth ? 12 : 6}>
          {getRadioGroup(
            field as DynamicRadioGroup,
            formData,
            setFormData,
            index,
            allSuggestedResponses
          )}
        </Grid>
      )
    case FieldTypes.FormGroup:
      return getFormGroup(
        field as DynamicFormGroup,
        formData,
        setFormData,
        index,
        allSuggestedResponses
      )
    case FieldTypes.FormControl:
      return (
        (field as FormSection).fields && (
          <Grid key={field.key} item xs={12} sm={fullWidth ? 12 : 6}>
            <FormControl
              fullWidth={field.fullWidth}
              component="fieldset"
              className={classes.formControl}
            >
              <FormLabel
                className={classes.formControlLabel}
                component="legend"
              >
                {field.label}
              </FormLabel>
              <DynamicFields
                fields={(field as FormSection).fields}
                index={index}
                fullWidth={field.fullWidth}
                useStyles={useStyles}
                fieldSuggestedResponses={allSuggestedResponses}
                theme={theme}
              />
              {field.helperText && (
                <FormHelperText>{field.helperText}</FormHelperText>
              )}
            </FormControl>
          </Grid>
        )
      )

    case FieldTypes.Date:
      return (
        <Grid key={field.key} item xs={12} sm={fullWidth ? 12 : 6}>
          {getDateField(
            field as DynamicDateField,
            formDataDate,
            setFormData,
            index
          )}
        </Grid>
      )

    case FieldTypes.Time:
      return (
        <Grid key={field.key} item xs={12} sm={fullWidth ? 12 : 6}>
          {getTimeField(
            field as DynamicDateField,
            formDataTime,
            setFormData,
            index
          )}
        </Grid>
      )
  }

  return <React.Fragment />
}

export default DynamicField
